import { useState, useEffect, useRef } from 'react'
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  getDocs,
} from 'firebase/firestore'
import { db } from '../../../firebase'
import './coursesAdmin.css'

const createLesson = () => ({
  tempId: crypto.randomUUID(),
  id: null,
  title: '',
  videoURL: '',
  files: [],
  order: '',
})

function CoursesAdmin() {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [type, setType] = useState('free')
  const [lessons, setLessons] = useState([createLesson()])
  const [courses, setCourses] = useState([])
  const [editCourseId, setEditCourseId] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [testLink, setTestLink] = useState('') //  новое поле
  const formRef = useRef(null)

  // Загрузка курсов
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'courses'), (snapshot) => {
      setCourses(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })))
    })
    return () => unsub()
  }, [])

  const filteredCourses = courses.filter((course) =>
    course.title?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Изменение урока
  const handleLessonChange = (tempId, field, value) => {
    setLessons((prev) =>
      prev.map((l) =>
        l.tempId === tempId ? { ...l, [field]: field === 'order' ? Number(value) : value } : l
      )
    )
  }

  const addLesson = () => setLessons((prev) => [...prev, createLesson()])
  const removeLesson = (tempId) => setLessons((prev) => prev.filter((l) => l.tempId !== tempId))

  // Файлы
  const addFile = (tempId) => {
    setLessons((prev) =>
      prev.map((l) =>
        l.tempId === tempId ? { ...l, files: [...l.files, { name: '', url: '' }] } : l
      )
    )
  }

  const handleFileChange = (tempId, index, field, value) => {
    setLessons((prev) =>
      prev.map((l) => {
        if (l.tempId !== tempId) return l
        const updatedFiles = [...l.files]
        updatedFiles[index][field] = value
        return { ...l, files: updatedFiles }
      })
    )
  }

  const removeFile = (tempId, index) => {
    setLessons((prev) =>
      prev.map((l) => {
        if (l.tempId !== tempId) return l
        const updatedFiles = l.files.filter((_, i) => i !== index)
        return { ...l, files: updatedFiles }
      })
    )
  }

  const convertToDownloadLink = (url) => {
    const match = url.match(/\/d\/(.*?)\//)
    if (!match) return url
    return `https://drive.google.com/uc?export=download&id=${match[1]}`
  }

  // Сохранение курса
  const handleSubmit = async (e) => {
    e.preventDefault()
    let courseId = editCourseId

    if (!editCourseId) {
      const ref = await addDoc(collection(db, 'courses'), {
        title,
        description,
        type,
        testLink, //  сохраняем ссылку
        createdAt: serverTimestamp(),
      })
      courseId = ref.id
    } else {
      await updateDoc(doc(db, 'courses', editCourseId), {
        title,
        description,
        type,
        testLink, //  обновляем ссылку
      })
    }

    // Удаляем старые уроки
    const lessonsSnap = await getDocs(collection(db, 'courses', courseId, 'lessons'))
    const existingIds = lessonsSnap.docs.map((d) => d.id)
    const currentIds = lessons.filter((l) => l.id).map((l) => l.id)
    for (const id of existingIds)
      if (!currentIds.includes(id)) await deleteDoc(doc(db, 'courses', courseId, 'lessons', id))

    // Сохраняем уроки
    for (const lesson of lessons) {
      const data = {
        title: lesson.title,
        videoURL: lesson.videoURL || '',
        order: lesson.order || null,
        files:
          lesson.files?.map((f) => ({ name: f.name, url: convertToDownloadLink(f.url) })) || [],
        createdAt: serverTimestamp(),
      }
      if (lesson.id) await updateDoc(doc(db, 'courses', courseId, 'lessons', lesson.id), data)
      else if (lesson.title.trim())
        await addDoc(collection(db, 'courses', courseId, 'lessons'), data)
    }

    resetForm()
  }

  const resetForm = () => {
    setTitle('')
    setDescription('')
    setType('free')
    setLessons([createLesson()])
    setEditCourseId(null)
    setTestLink('') // сброс поля
  }

  const editCourse = async (course) => {
    setTitle(course.title)
    setDescription(course.description)
    setType(course.type)
    setEditCourseId(course.id)
    setTestLink(course.testLink || '') // подхватываем ссылку при редактировании

    const snap = await getDocs(collection(db, 'courses', course.id, 'lessons'))
    setLessons(
      snap.docs.length
        ? snap.docs.map((d) => ({ tempId: crypto.randomUUID(), id: d.id, ...d.data() }))
        : [createLesson()]
    )

    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth' }), 0)
  }

  const deleteCourse = async (id) => {
    if (!window.confirm('Удалить курс полностью?')) return
    const lessonsSnap = await getDocs(collection(db, 'courses', id, 'lessons'))
    for (const l of lessonsSnap.docs) await deleteDoc(doc(db, 'courses', id, 'lessons', l.id))
    await deleteDoc(doc(db, 'courses', id))
  }

  const downloadFile = (file) => {
    if (!file?.url) return
    window.open(file.url)
  }

  return (
    <div ref={formRef}>
      <h2>{editCourseId ? 'Редактировать курс' : 'Добавить курс'}</h2>
      <form onSubmit={handleSubmit}>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          placeholder="Название курса"
        />
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
          placeholder="Описание курса"
        />
        <select value={type} onChange={(e) => setType(e.target.value)}>
          <option value="free">Бесплатный</option>
          <option value="paid">Платный</option>
        </select>

        <input
          value={testLink}
          onChange={(e) => setTestLink(e.target.value)}
          placeholder="Ссылка на тест Google Forms"
          style={{ marginTop: '20px' }}
        />

        <h3>Уроки</h3>
        {lessons.map((l) => (
          <div key={l.tempId} className="lesson-card">
            <textarea
              value={l.title}
              onChange={(e) => handleLessonChange(l.tempId, 'title', e.target.value)}
              placeholder="Тема урока"
            />
            <input
              value={l.videoURL}
              onChange={(e) => handleLessonChange(l.tempId, 'videoURL', e.target.value)}
              placeholder="Ссылка на видео"
            />
            <input
              type="number"
              value={l.order || ''}
              onChange={(e) => handleLessonChange(l.tempId, 'order', e.target.value)}
              placeholder="Порядок урока"
            />

            <h4>Файлы (Google Drive)</h4>
            {l.files.map((f, idx) => (
              <div key={idx} className="lesson-file-card">
                <input
                  value={f.name}
                  onChange={(e) => handleFileChange(l.tempId, idx, 'name', e.target.value)}
                  placeholder="Название файла"
                />
                <input
                  value={f.url}
                  onChange={(e) => handleFileChange(l.tempId, idx, 'url', e.target.value)}
                  placeholder="Ссылка Google Drive"
                />
                <button type="button" onClick={() => downloadFile(f)}>
                  Скачать
                </button>
                <button type="button" onClick={() => removeFile(l.tempId, idx)}>
                  Удалить файл
                </button>
              </div>
            ))}
            <button type="button" onClick={() => addFile(l.tempId)}>
              Добавить файл
            </button>
            <button type="button" onClick={() => removeLesson(l.tempId)}>
              Удалить урок
            </button>
          </div>
        ))}

        <button type="button" onClick={addLesson}>
          Добавить урок
        </button>
        <button type="submit">Сохранить курс</button>
      </form>

      <hr />
      <h3>Поиск курсов</h3>
      <input
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Поиск курса..."
      />
      <div className="courses-nav-details-container">
        {filteredCourses.map((course) => (
          <div key={course.id} className="courses-nav-details-admin">
            <b>{course.title}</b>
            <div>
              <button onClick={() => editCourse(course)}>Редактировать</button>
              <button onClick={() => deleteCourse(course.id)}>Удалить</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default CoursesAdmin
